import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "./Loading";
import warehouseService from "../Services/warehouseService";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MdOutlineFileDownload } from "react-icons/md";
import SecondaryButton from "./SecondaryButton";

const SalesDetailsModal = ({ isOpen, onClose, vendorCode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !vendorCode) return;

      setLoading(true);
      setError(false);
      try {
        const res =
          await warehouseService.fetchSalesQtyByVendorCode(vendorCode);
        setRows(res?.sales || []);
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, vendorCode]);

  if (!isOpen) return null;

  const handleView = (saleId) => {
    navigate(`/order/view?invoice=${saleId}`);
  };

  const handleDownloadExcel = () => {
    try {
      if (!rows || rows.length === 0) {
        alert("No data to export!");
        return;
      }

      const excelRows = rows.map((r, idx) => ({
        "S.No": idx + 1,
        "Order No.": r.order_id ?? "",
        "Order Date": r.order_date
          ? new Date(r.order_date).toLocaleDateString()
          : "",
        "Part Number": r.vendor_code ?? "",
        Brand: r.brand ?? "",
        Title: r.title ?? "",
        Qty: Number(r.invoice_quantity ?? 0),
        Price: Number(r.price ?? 0),
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelRows);

      worksheet["!cols"] = [
        { wch: 6 },
        { wch: 14 },
        { wch: 14 },
        { wch: 18 },
        { wch: 14 },
        { wch: 40 },
        { wch: 10 },
        { wch: 12 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Details");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");

      saveAs(blob, `sales-details-${vendorCode}-${yyyy}-${mm}-${dd}.xlsx`);
    } catch (e) {
      console.error("Excel export failed:", e);
      alert("Excel export failed. Check console.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white w-[92%] max-w-6xl rounded-2xl shadow-xl p-6 relative">
        <div className="flex flex-row justify-between items-start">
          <div>
            <h1 className="font-bold text-3xl text-secondary">Sale Details</h1>
            <div className="mt-2 px-4 py-2 rounded-md shadow-md border w-fit">
              <p className="text-sm text-gray-500">
                Part Number:{" "}
                <span className="text-gray-800 font-semibold">
                  {vendorCode}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-red-600 duration-300 text-xl"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="py-10">
            <Loading />
          </div>
        ) : error ? (
          <p className="text-red-600 font-semibold">
            Failed to load sales details.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-gray-500">No sales records found.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto border rounded-xl mt-10 scorllbar-hide no-scrollbar">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-gray-800 border-b text-white">
                <tr className="text-left">
                  <th className="p-3">#</th>
                  <th className="p-3">Order No.</th>
                  <th className="p-3">Order Date</th>
                  <th className="p-3">Part Number</th>
                  <th className="p-3">Brand</th>
                  <th className="p-3">Title</th>
                  <th className="p-3 text-center">Qty</th>
                  <th className="p-3 text-center">Price</th>
                  <th className="p-3 text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`${r.id}-${idx}`} className="border-b">
                    <td className="p-3">{idx + 1}</td>
                    <td className="p-3">{r.order_id}</td>
                    <td className="p-3">
                      {r.order_date
                        ? new Date(r.order_date).toLocaleDateString()
                        : ""}
                    </td>
                    <td className="p-3">{r.vendor_code}</td>
                    <td className="p-3">{r.brand}</td>
                    <td className="p-3">{r.title}</td>
                    <td className="p-3 text-center">{r.invoice_quantity}</td>
                    <td className="p-3 text-center">{r.price}</td>
                    <td className="p-3 text-center">
                      <button
                        type="button"
                        onClick={() => handleView(r.invoice)}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98] transition"
                      >
                        View Order
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end mt-10">
          <SecondaryButton
            text="Download Excel"
            Icon={MdOutlineFileDownload}
            onClick={handleDownloadExcel}
          />
        </div>
      </div>
    </div>
  );
};

export default SalesDetailsModal;
